import { AutocompleteState, createAutocomplete, AutocompleteApi } from "@algolia/autocomplete-core";
import { useStorefrontConfig } from "../types/storefront";
import { ReactComponent as SearchIcon } from "images/icons/search-alternate.svg";
import { ReactComponent as RemoveIcon } from "images/icons/remove.svg";
import ProductAutocompleteConfig, { ProductResult } from "src/components/autocomplete/product_autocomplete_config";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import debounce from "src/debounce";
import AutocompleteRenderer from "src/components/autocomplete_renderer";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { FocusProduct, UpdateSearch, useNavigationState } from "../types/navigation";
import { createPortal } from "react-dom";
import { TopSearch } from "@algolia/client-analytics";
import { ALGOLIA_PRODUCTS_RESULTS_NUMBER } from "src/components/autocomplete/autocomplete_config";

// these probably need to be changed to PRODUCTS_SEARCH
const DESKTOP_PRODUCT_SEARCH_INPUT_ID = "store_search_bar_desktop";
const MOBILE_PRODUCT_SEARCH_INPUT_ID = "store_search_bar_mobile";

const SearchPage = () => {
  const config = useStorefrontConfig();

  const [navigationState, updateNavigation] = useNavigationState();
  const [topSearchResult, setTopSearchResult] = useState([]);
  const [topRecommendationResult, setTopRecommendationResult] = useState<ProductResult[]>([]);

  const fetchTopSearchResults = useCallback(async () => {
    const response = await fetch(`/storefront/${config.storefrontId}/top_searches`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accepts": "application/json",
      },
    });

    const { data } = await response.json();

    const topSearches = (data.searches || []).filter((topSearch: TopSearch) => {
      const matched = topSearch.search.match(/[^a-zA-Z ]/g)
      return !matched && topSearch.count > 5 && topSearch.search.length > 2
    });

    setTopSearchResult(topSearches);
  }, [config.storefrontId])

  const fetchTopTrendingRecommendationsFromServer = useCallback(async () => {
    const response = await fetch(`/storefronts/${config.storefrontId}/top_recommendations`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accepts": "application/json",
      },
    });

    const { data } = await response.json();

    setTopRecommendationResult(data as ProductResult[] || [])
  }, [config.storefrontId])

  useEffect(() => {
    fetchTopTrendingRecommendationsFromServer();
    fetchTopSearchResults();
  }, [fetchTopSearchResults, fetchTopTrendingRecommendationsFromServer])

  const autocompleteConfig = useMemo(() => new ProductAutocompleteConfig({
    indexName: config.searchIndexName,
    storeId: Number.parseInt(config.storefrontId),
    storePermalink: config.storefrontPermalink,
    selectedFulfillmentMethod: config.fulfillmentMethod,
    imageBaseUrl: config.imageBaseUrl,
    onResultClick: (fullEncodedKeyPath: string[], queryId: string | undefined) => { updateNavigation(FocusProduct.action(fullEncodedKeyPath, true, queryId)) },
    onTopSearchClick: (newQuery: string) => { 
      updateNavigation(UpdateSearch.action(newQuery));
      // hard setting the value here as we are using defaultValue.
      const mobileInput = document.getElementById(MOBILE_PRODUCT_SEARCH_INPUT_ID);
      const desktopInput = document.getElementById(DESKTOP_PRODUCT_SEARCH_INPUT_ID);
      ([mobileInput, desktopInput]).map(inputElement => ((inputElement as HTMLInputElement).value = newQuery));
    },
    searchAppId: config.searchAppId,
    searchPublicApiKey: config.searchPublicApiKey,
    topSearchFetchResult: topSearchResult,
    topRecommendations: topRecommendationResult,
  }), [config.fulfillmentMethod, config.searchIndexName, config.storefrontPermalink, config.storefrontId, config.imageBaseUrl, updateNavigation, config.searchAppId, config.searchPublicApiKey, topSearchResult, topRecommendationResult]);

  const searchClient = useMemo(() => algoliasearch(config.searchAppId, config.searchPublicApiKey, { baseHeaders: {
      'X-Algolia-UserToken': config.searchCustomerToken,
    }
  }), [config.searchAppId, config.searchPublicApiKey, config.searchCustomerToken]);

  const [autocomplete, setAutocomplete] = useState<AutocompleteApi<ProductResult>>();
  const [autocompleteState, setAutocompleteState] = React.useState<AutocompleteState<ProductResult>>({
    activeItemId: null,
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: "",
    status: "idle",
  });

  const navInputContainer = useMemo(() => { return document.querySelector("#autocomplete-input-nav"); }, []);
  const navResultsSectionWrapper = useMemo(() => { return document.querySelector("#autocomplete-results-section-wrapper-nav"); }, []);
  const navResultsContainer = useMemo(() => { return document.querySelector("#autocomplete-results-container-nav"); }, []);

  const showDesktopResultsContainer = useCallback(() => {
    navResultsSectionWrapper?.classList?.remove("is-hidden-above-desktop");
  }, [navResultsSectionWrapper?.classList]);

  const hideDesktopResultsContainer = useCallback(() => {
    navResultsSectionWrapper?.classList?.add("is-hidden-above-desktop");
  }, [navResultsSectionWrapper?.classList]);

  useEffect(() => {
    setAutocomplete(
      createAutocomplete<ProductResult>({
        onStateChange({ state }) {
          setAutocompleteState(state);
        },
        openOnFocus: true,
        insights: true,
        getSources({ query, setContext }) {
          if (!query) {
            return [autocompleteConfig.emptyQueryResults()];
          }

          return [autocompleteConfig.queryResults(searchClient, setContext)]
        },
      })
    );
  }, [autocompleteConfig, searchClient, setAutocompleteState]);

  useEffect(() => {
    if (!autocomplete) return;

    autocomplete.setQuery(navigationState.searchQuery);

    autocomplete.refresh();
  }, [navigationState.searchQuery, autocomplete, navResultsSectionWrapper?.classList]);

  if (topRecommendationResult.length >= 3 || topSearchResult.length > 2 || autocompleteState.query.length > 0) {
    showDesktopResultsContainer();
  } else {
    hideDesktopResultsContainer();
  }

  const updateQuery = useCallback((query: string) => {
    if (!autocomplete) return;

    autocompleteConfig.numResults = ALGOLIA_PRODUCTS_RESULTS_NUMBER;
    autocomplete.setQuery(query);
    updateNavigation(UpdateSearch.action(query));

    autocomplete.refresh().then(() => {
      if (query.length === 0) {
        hideDesktopResultsContainer()
      } else if (query.length > 0) {
        showDesktopResultsContainer()
      }
    });

  }, [autocomplete, hideDesktopResultsContainer, showDesktopResultsContainer, updateNavigation, autocompleteConfig]);

  const [debounceUpdateQuery, cancelDebouncedUpdateQuery] = useMemo(() => debounce(updateQuery, 200), [updateQuery]);

  const clearSearchQuery = useCallback(() => {
    if (!autocomplete) return;

    autocompleteConfig.numResults = ALGOLIA_PRODUCTS_RESULTS_NUMBER;
    autocomplete.setQuery("");
    updateNavigation(UpdateSearch.action(""));

    cancelDebouncedUpdateQuery();
    autocomplete.refresh();
  }, [autocomplete, cancelDebouncedUpdateQuery, updateNavigation, autocompleteConfig]);

  const onQueryInputChange = useCallback((event: React.FormEvent) => {
    if (!autocomplete) return;

    cancelDebouncedUpdateQuery();
    debounceUpdateQuery((event.currentTarget as HTMLInputElement).value);
  }, [autocomplete, cancelDebouncedUpdateQuery, debounceUpdateQuery]);

  const updateNavigationSearchFnc = useCallback(() => {
    updateNavigation(UpdateSearch.action(""));
  }, [updateNavigation])

  const inputElement = useCallback((id: string) => {
    return (
      <>
        <label htmlFor="store_search_bar">Search {config.storefrontName}</label>
        <span>
        <SearchIcon className="icon streamline-icon"/>

        <input
          type="search"
          name="store_search_bar"
          id={id}
          placeholder={`Search ${config.storefrontName}`}
          style={{ "--input-control-character-offset": 0 } as React.CSSProperties}
          required
          defaultValue={navigationState.searchQuery}
          autoComplete="off"
          onInput={onQueryInputChange}
          data-clearable-input-target="input"
        />

        <div className="input-controls">
          <button className="button is-icon-only is-ghost" onClick={clearSearchQuery} data-action="click->clearable-input#clearInput">
            <RemoveIcon className="icon streamline-icon"/>
          </button>
        </div>
      </span>
      </>
    );
  }, [clearSearchQuery, config.storefrontName, onQueryInputChange, navigationState.searchQuery])

  const expandResults = useCallback((event: React.MouseEvent, numResults: number) => {
    autocompleteConfig.expandResults(event, numResults);
    autocomplete?.refresh();
  }, [autocompleteConfig, autocomplete]);

  const autocompleteRenderer = useMemo(() => {
    return <AutocompleteRenderer
      autocompleteState={autocompleteState}
      autocompleteConfig={autocompleteConfig}
      dialogType="catalog"
      updateNavigationSearchFnc={updateNavigationSearchFnc}
      expandResults={expandResults}
    />
  }, [autocompleteConfig, autocompleteState, updateNavigationSearchFnc, expandResults])

  return (
    <div
      className="app-view scrollable-area"
      id="search"
    >
      <section className="is-stuck-top is-layer-middle has-gradient-reversed">
        <div className="container">
          <div className="text-input has-prefix-icon is-muted-icon has-hidden-label" data-controller="clearable-input">
            {inputElement(MOBILE_PRODUCT_SEARCH_INPUT_ID)}
            {navInputContainer ? createPortal(inputElement(DESKTOP_PRODUCT_SEARCH_INPUT_ID), navInputContainer): null}
          </div>
        </div>
      </section>

      <hr className="space-mt-none space-mb-none"/>

      <div id="autocomplete-results">
        {autocompleteRenderer}

        {navResultsContainer ? createPortal(autocompleteRenderer, navResultsContainer) : null}
      </div>
    </div>
  )
}

export { SearchPage, MOBILE_PRODUCT_SEARCH_INPUT_ID };
